import React, { useEffect, useState } from 'react';
import { Spin, Switch, Tooltip, Select, Input } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { updateJeAuditSuffixSetting } from 'redux/action/companyAction';
import { AppDispatch } from 'redux/store';
import { toastText } from 'utils/utils';
import styles from './index.module.scss';

const DEFAULT_JE_AUDIT_SUFFIX = ' | Posted by CostAllocation Pro AI';

interface CompanySettingsProps {
	isExcludeEmployeeEnabled: boolean;
	onToggleExclude: (checked: boolean) => void;
	isSummarizedJournalViewEnabled: boolean;
	onToggleSummarizedView: (checked: boolean) => void;
	isExcludeEmployeeLoading: boolean;
	isSummarizedJournalViewLoading: boolean;
	isJournalEntriesPermissionAdd: boolean;
	isJournalEntriesPermissionEdit: boolean;
	isTimeHoursDecimal: boolean;
	onTimeHoursDecimalChange: (value: boolean) => void;
	isTimeHoursDecimalLoading: boolean;
}

const CompanySettings: React.FC<CompanySettingsProps> = ({
	isExcludeEmployeeEnabled,
	onToggleExclude,
	isSummarizedJournalViewEnabled,
	onToggleSummarizedView,
	isExcludeEmployeeLoading,
	isSummarizedJournalViewLoading,
	isJournalEntriesPermissionAdd,
	isJournalEntriesPermissionEdit,
	isTimeHoursDecimal,
	onTimeHoursDecimalChange,
	isTimeHoursDecimalLoading,
}) => {
	const dispatch = useDispatch<AppDispatch>();
	const currentCompany = useSelector((state: any) => state.companies?.currentCompany);
	// Spec §6: default ON so the AI origin is visible inside QBO.
	const jeAuditSuffixEnabled: boolean = currentCompany?.jeAuditSuffixEnabled ?? true;
	const savedSuffix: string = currentCompany?.jeAuditSuffix ?? '';
	const [suffixDraft, setSuffixDraft] = useState(savedSuffix);
	const [auditSuffixLoading, setAuditSuffixLoading] = useState(false);
	const canEditJournalSettings = isJournalEntriesPermissionAdd && isJournalEntriesPermissionEdit;

	useEffect(() => {
		setSuffixDraft(savedSuffix);
	}, [savedSuffix]);

	const saveAuditSuffix = async (enabled: boolean, suffix: string) => {
		if (!currentCompany?.id) {
			return;
		}
		setAuditSuffixLoading(true);
		// Empty → null so the backend falls back to the default suffix.
		const result: any = await dispatch(
			updateJeAuditSuffixSetting({
				companyId: currentCompany.id,
				jeAuditSuffixEnabled: enabled,
				jeAuditSuffix: suffix.length ? suffix : null,
			})
		);
		setAuditSuffixLoading(false);
		if (updateJeAuditSuffixSetting.rejected.match(result)) {
			toastText((result.payload as any)?.message || 'Could not save the audit note setting.', 'error');
			setSuffixDraft(savedSuffix);
		}
	};

	return (
		<>
			<div className={styles.settingsCard}>
				<h4 className={styles.sectionTitle}>Journal Entry Settings</h4>

				<div className={styles.settingRow}>
					<span className={styles.label}>
						Exclude Employee Name
						<Tooltip
							title={
								<span style={{ fontSize: '12px' }}>
									This setting excludes Employee Name from your journal
								</span>
							}
						>
							<InfoCircleOutlined style={{ marginLeft: 6, color: '#888' }} />
						</Tooltip>
					</span>

					<Spin spinning={isExcludeEmployeeLoading} size="small">
						<Switch
							checked={isExcludeEmployeeEnabled}
							onChange={onToggleExclude}
							disabled={
								isExcludeEmployeeLoading ||
								!isJournalEntriesPermissionAdd ||
								!isJournalEntriesPermissionEdit
							}
						/>
					</Spin>
				</div>

				<div className={styles.settingRow}>
					<span className={styles.label}>
						Expense Allocation Journal Summarized View
						<Tooltip
							title={
								<span style={{ fontSize: '12px' }}>
									This setting enables the summarized view for the expense allocation journal.
								</span>
							}
						>
							<InfoCircleOutlined style={{ marginLeft: 6, color: '#888' }} />
						</Tooltip>
					</span>
					<Spin spinning={isSummarizedJournalViewLoading} size="small">
						<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
							<Switch
								checked={isSummarizedJournalViewEnabled}
								onChange={onToggleSummarizedView}
								disabled={
									isSummarizedJournalViewLoading ||
									!isJournalEntriesPermissionAdd ||
									!isJournalEntriesPermissionEdit
								}
							/>
						</div>
					</Spin>
				</div>

				<div className={styles.settingRow}>
					<span className={styles.label}>
						Add audit note to AI-posted journals
						<Tooltip
							title={
								<span style={{ fontSize: '12px' }}>
									Appends a note to the memo (Private Note) of journals the CostAllocation Pro AI
									assistant posts to QuickBooks, so their origin is visible in QuickBooks.
								</span>
							}
						>
							<InfoCircleOutlined style={{ marginLeft: 6, color: '#888' }} />
						</Tooltip>
					</span>
					<Spin spinning={auditSuffixLoading} size="small">
						<Switch
							checked={jeAuditSuffixEnabled}
							onChange={(checked) => saveAuditSuffix(checked, suffixDraft.trim() ? suffixDraft : '')}
							disabled={auditSuffixLoading || !canEditJournalSettings}
						/>
					</Spin>
				</div>
				{jeAuditSuffixEnabled && (
					<div className={styles.settingRow}>
						<span className={styles.label}>Audit note text (optional)</span>
						<Input
							style={{ maxWidth: 320 }}
							value={suffixDraft}
							maxLength={100}
							placeholder={DEFAULT_JE_AUDIT_SUFFIX}
							disabled={auditSuffixLoading || !canEditJournalSettings}
							onChange={(e) => setSuffixDraft(e.target.value)}
							onBlur={() => {
								const next = suffixDraft.trim() ? suffixDraft : '';
								if (next !== savedSuffix) {
									saveAuditSuffix(true, next);
								}
							}}
							onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
						/>
					</div>
				)}
			</div>

			<div className={styles.settingsCard}>
				<h4 className={styles.sectionTitle}>Export Settings</h4>
				<div className={styles.settingRow}>
					<span className={styles.label}>
						Export Time Hour Format
						<Tooltip
							title={
								<span style={{ fontSize: '12px' }}>
									This setting determines the format for time hours in exports: HH:MM or decimal
								</span>
							}
						>
							<InfoCircleOutlined style={{ marginLeft: 6, color: '#888' }} />
						</Tooltip>
					</span>
					<Spin spinning={isTimeHoursDecimalLoading} size="small">
						<Select
							value={isTimeHoursDecimal ? 'decimal' : 'time'}
							style={{ width: 150 }}
							onChange={(value) => onTimeHoursDecimalChange(value === 'decimal')}
							disabled={
								isTimeHoursDecimalLoading ||
								!isJournalEntriesPermissionAdd ||
								!isJournalEntriesPermissionEdit
							}
							options={[
								{ value: 'time', label: 'HH:MM' },
								{ value: 'decimal', label: 'Decimal' },
							]}
						/>
					</Spin>
				</div>
			</div>
		</>
	);
};

export default CompanySettings;
